import cx from 'classnames';
import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { usePopper } from 'react-popper';
import { Placement } from '@popperjs/core';

import styles from './Tooltip.module.scss';

export type Trigger = 'hover' | 'click' | 'focus' | 'disabled' | 'always';

export interface Props {
  /** Whether the tooltip should be displayed as a block element */
  block?: boolean;
  children?: React.ReactNode;
  className?: Parameters<typeof cx>[0];
  /** Whether the element contains an element that should respond to a click, such as a link */
  clickable?: boolean;
  /** Whether the tooltip should be displayed in dark mode */
  dark?: boolean;
  id?: string;
  isOpen?: boolean;
  /** Where the tooltip should be placed in relation to its triggering element */
  placement: Placement;
  /** When the tooltip should be displayed */
  showOn: Trigger | Trigger[];
  /** Whether the tooltip should render smaller than the default */
  small?: boolean;
  style?: CSSProperties;
  /** The element that should trigger the tooltip */
  target: React.ReactElement;
  /** ClassName to apply to the element that wraps both the trigger and the tooltip  */
  wrapperClassName?: Parameters<typeof cx>[0];
  /** ClassName to apply to the element that wraps the trigger */
  triggerWrapperClassName?: Parameters<typeof cx>[0];

  // https://popper.js.org/docs/v2/modifiers/offset/
  offset?: [number, number];
  /** Action to perform when the tooltip is shown */
  onShow?: () => void;
  /** Action to perform when the tooltip is hidden */
  onHide?: () => void;
  /** Action to perform when the tooltip is hidden by pressing the Escape key */
  onEscape?: () => void;
}

interface WrapperProps {
  onClick?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onPointerEnter?: () => void;
  onPointerLeave?: (e: React.PointerEvent) => void;
}

enum TooltipState {
  VISIBLE = 'visible',
  HIDDEN = 'hidden',
}

enum TriggerState {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export const Tooltip: React.FC<Props> = props => {
  const [targetElement, setTargetElement] = useState<HTMLDivElement | null>(
    null,
  );
  const [tooltipElement, setTooltipElement] = useState<HTMLDivElement | null>(
    null,
  );

  // Since props.showOn could either be a string or an array, we use indexOf, works for both
  const showOnHover = props.showOn.indexOf('hover') > -1;

  const popper = usePopper(targetElement, tooltipElement, {
    placement: props.placement,
    modifiers: [
      {
        name: 'offset',
        options: {
          // https://popper.js.org/docs/v2/modifiers/offset/
          offset: props.offset || [0, 4],
        },
      },
    ],
  });

  const debounce = (callback: (...args: any[]) => void) => {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        callback(...args);
      }, 100);
    };
  };

  const updatePopper = useCallback(() => {
    if (typeof popper.forceUpdate === 'function') {
      popper.forceUpdate();
    }
  }, [popper]);

  // Debounce the ResizeObserver callback to avoid unnecessary updates
  const delayedUpdatePopper = debounce(updatePopper);

  // Use a ResizeObserver to update the popper when the target element resizes
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      delayedUpdatePopper();
    });

    if (wrapperElement.current) {
      resizeObserver.observe(wrapperElement.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [delayedUpdatePopper]);

  const [tooltipState, setTooltipState] = useState<TooltipState>(
    !!props.isOpen ? TooltipState.VISIBLE : TooltipState.HIDDEN,
  );
  const [triggerState, setTriggerState] = useState<TriggerState>(
    !!props.isOpen ? TriggerState.ACTIVE : TriggerState.INACTIVE,
  );

  const hoverHideTimeout = useRef<number>();
  const hoverShowTimeout = useRef<number>();

  const wrapperElement = useRef<HTMLDivElement | null>(null);

  const toggleShowTooltip = () => {
    updatePopper();
    setTriggerState(TriggerState.ACTIVE);
  };

  const clickHandler = useCallback(
    (event: MouseEvent) => {
      delayedUpdatePopper();
      if (
        tooltipElement &&
        event.target instanceof Node &&
        props.showOn.indexOf('always') === -1
      ) {
        if (
          !(
            tooltipElement.contains(event.target) ||
            targetElement?.contains(event.target)
          ) ||
          (props.showOn.indexOf('click') === -1 &&
            props.showOn.indexOf('focus') === -1)
        ) {
          props.onEscape && props.onEscape();
          setTriggerState(TriggerState.INACTIVE);
          setTooltipState(TooltipState.HIDDEN);
          clearTimeout(hoverHideTimeout.current);
          clearTimeout(hoverShowTimeout.current);
        }
      }
    },
    [props, targetElement, tooltipElement, delayedUpdatePopper],
  );

  const escapeKeyHandler = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        props.onEscape && props.onEscape();
        setTriggerState(TriggerState.INACTIVE);
      }
    },
    [props],
  );

  const onTooltipMouseEnter = useCallback(() => {
    setTriggerState(TriggerState.ACTIVE);
  }, []);

  const onTooltipMouseLeave = () => {
    setTriggerState(TriggerState.INACTIVE);
  };

  useEffect(() => {
    if (props.showOn === 'disabled') {
      return;
    }

    if ((props.clickable || showOnHover) && tooltipElement) {
      tooltipElement.addEventListener('mouseenter', onTooltipMouseEnter);
      tooltipElement.addEventListener('mouseleave', onTooltipMouseLeave);
    }

    document.addEventListener('click', clickHandler);
    document.addEventListener('keyup', escapeKeyHandler);

    return () => {
      if ((props.clickable || showOnHover) && tooltipElement) {
        tooltipElement.removeEventListener('mouseenter', e =>
          onTooltipMouseEnter(),
        );
        tooltipElement.removeEventListener('mouseleave', onTooltipMouseLeave);
      }

      document.removeEventListener('click', clickHandler);
      document.removeEventListener('keyup', escapeKeyHandler);
    };
  });

  const wrapperProps: WrapperProps = {};

  [props.showOn].flat().forEach((trigger: Trigger) => {
    switch (trigger) {
      case 'click':
        wrapperProps.onClick = toggleShowTooltip;
        break;
      case 'focus':
        wrapperProps.onFocus = () => setTriggerState(TriggerState.ACTIVE);
        wrapperProps.onBlur = () => setTriggerState(TriggerState.INACTIVE);
        break;
      case 'hover':
        wrapperProps.onPointerEnter = () =>
          setTriggerState(TriggerState.ACTIVE);
        wrapperProps.onPointerLeave = () => {
          clearTimeout(hoverShowTimeout.current);
          setTriggerState(TriggerState.INACTIVE);
        };
    }
  });

  useEffect(() => {
    if (props.showOn === 'always') {
      return;
    }
    clearTimeout(hoverHideTimeout.current);
    clearTimeout(hoverShowTimeout.current);
    hoverShowTimeout.current = window.setTimeout(
      () => {
        if (
          tooltipState === TooltipState.HIDDEN &&
          triggerState === TriggerState.ACTIVE
        ) {
          setTooltipState(TooltipState.VISIBLE);
          props.onShow && props.onShow();
        }
      },
      showOnHover ? 500 : 0,
    );
    if (triggerState === TriggerState.INACTIVE) {
      hoverHideTimeout.current = window.setTimeout(() => {
        setTooltipState(TooltipState.HIDDEN);
        props.onHide && props.onHide();
      }, 100);
    }

    return () => {
      clearTimeout(hoverShowTimeout.current);
      clearTimeout(hoverHideTimeout.current);
    };
  }, [triggerState, tooltipState, props, showOnHover]);

  return (
    <div
      className={cx(styles.tooltipWrapper, props.wrapperClassName)}
      ref={wrapperElement}
    >
      <div
        className={cx(
          styles.triggerWrapper,
          props.triggerWrapperClassName,
          props.block && styles.block,
        )}
        ref={setTargetElement}
        {...wrapperProps}
      >
        {props.target}
      </div>
      <div
        style={popper.styles.popper}
        className={cx(
          styles.tooltip,
          props.small && styles.small,
          props.dark && styles.dark,
          tooltipState === TooltipState.VISIBLE && styles.showTooltip,
          props.className,
        )}
        role='tooltip'
        {...popper.attributes.popper}
        ref={setTooltipElement}
      >
        {typeof props.children === 'string' ? (
          <span className={styles.text}>{props.children}</span>
        ) : (
          props.children
        )}
      </div>
    </div>
  );
};

Tooltip.displayName = 'Tooltip';
export default Tooltip;
export { styles };
